import { ChangeDetectionStrategy, Component, effect, inject, input, output } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, UntypedFormArray, Validators } from '@angular/forms';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { AffiliateResponse, CreateAffiliateRequest } from '../../affiliate.model';
import { LookupResponse } from '@core/models/common.model';
import { InterventionStore } from '@features/intervention/intervention.store';
import { SHARED_UTILS } from '@shared/modules/shared-utils.module';
import { nullIfBlank } from '@shared/utils/payload-utils';
@Component({
  selector: 'affiliate-form',
  imports: [ReactiveFormsModule, ...SharedMaterialUIModule],
  templateUrl: './affiliate-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AffiliateFormComponent {
  private readonly formBuilder = inject(FormBuilder);
  protected readonly interventionStore = inject(InterventionStore);

  readonly title = input('Affiliate form');
  readonly submitLabel = input('Save');
  readonly busy = input(false);
  readonly readOnly = input(false);
  readonly initialValue = input<AffiliateResponse | null>(null);
  readonly genders = input<any[]>([]);
  readonly languages = input<any[]>([]);
  readonly religions = input<any[]>([]);
  readonly affiliateDisciplines = input<any[]>([]);
  readonly provinces = input<LookupResponse[]>([]);

  readonly submitted = output<CreateAffiliateRequest>();

  readonly utils = SHARED_UTILS();

  // Step 1: Personal details
  readonly personalDetailsForm = this.formBuilder.group({
    name: ['', Validators.required],
    surname: ['', Validators.required],
    age: [null as number | null],
    genderId: [''],
    languageId: [''],
    religionId: [''],
  });

  // Step 2: Contact details
  readonly contactDetailsForm = this.formBuilder.group({
    address: [''],
    emailAddress: [''],
    contactNumber: [''],
  });

  // Step 3: Professional details
  readonly professionalDetailsForm = this.formBuilder.group({
    affiliateDisciplineId: [''],
    experience: [null as number | null],
    therapyModalities: [''],
    bhfRegistration: [''],
    regulatoryRegistrationNumber: [''],
    companyName: [''],
    trainedByAffiliate: [false],
    interventionTypeIds: [[] as string[]],
    isAvailableForReferrals: [true],
    maxActiveCaseload: [null as number | null, Validators.min(0)],
  });

  // Step 4: Accessibility
  readonly accessibilityForm = this.formBuilder.group({
    wheelchairFriendly: [false],
    signLanguageFriendly: [false],
  });

  readonly suburbsForm = new UntypedFormArray([]);

  readonly affiliateForm = this.formBuilder.group({
    personalDetails: this.personalDetailsForm,
    contactDetails: this.contactDetailsForm,
    professionalDetails: this.professionalDetailsForm,
    accessibility: this.accessibilityForm,
    suburbs: this.suburbsForm,
  });

  constructor() {
    this.interventionStore.loadInterventionTypes();

    effect(() => {
      const affiliate = this.initialValue();
      const isReadOnly = this.readOnly();

      this.affiliateForm.reset({
        personalDetails: {
          name: affiliate?.name ?? '',
          surname: affiliate?.surname ?? '',
          age: affiliate?.age ?? null,
          genderId: affiliate?.genderId ?? '',
          languageId: affiliate?.languageId ?? '',
          religionId: affiliate?.religionId ?? '',
        },
        contactDetails: {
          address: affiliate?.address ?? '',
          emailAddress: affiliate?.emailAddress ?? '',
          contactNumber: affiliate?.contactNumber ?? '',
        },
        professionalDetails: {
          affiliateDisciplineId: affiliate?.affiliateDisciplineId ?? '',
          experience: affiliate?.experience ?? null,
          therapyModalities: affiliate?.therapyModalities ?? '',
          bhfRegistration: affiliate?.bhfRegistration ?? '',
          regulatoryRegistrationNumber: affiliate?.regulatoryRegistrationNumber ?? '',
          companyName: affiliate?.companyName ?? '',
          trainedByAffiliate: affiliate?.trainedByAffiliate ?? false,
          interventionTypeIds: affiliate?.interventionTypes.map((item) => item.interventionTypeId) ?? [],
          isAvailableForReferrals: affiliate?.isAvailableForReferrals ?? true,
          maxActiveCaseload: affiliate?.maxActiveCaseload ?? null,
        },
        accessibility: {
          wheelchairFriendly: affiliate?.wheelchairFriendly ?? false,
          signLanguageFriendly: affiliate?.signLanguageFriendly ?? false,
        },
      }, { emitEvent: false });

      this.suburbsForm.clear({ emitEvent: false });
      for (const suburb of affiliate?.suburbs ?? []) {
        this.addSuburb(suburb);
      }

      if (isReadOnly) {
        this.affiliateForm.disable({ emitEvent: false });
      } else {
        this.affiliateForm.enable({ emitEvent: false });
      }
    });
  }

  protected submit(): void {
    if (this.affiliateForm.invalid) {
      this.affiliateForm.markAllAsTouched();
      return;
    }

    const { personalDetails, contactDetails, professionalDetails, accessibility } = this.affiliateForm.getRawValue();
    const request: CreateAffiliateRequest = {
      name: (personalDetails.name ?? '').trim(),
      surname: (personalDetails.surname ?? '').trim(),
      address: nullIfBlank(contactDetails.address),
      emailAddress: nullIfBlank(contactDetails.emailAddress),
      contactNumber: nullIfBlank(contactDetails.contactNumber),
      age: personalDetails.age ?? null,
      genderId: personalDetails.genderId || null,
      languageId: personalDetails.languageId || null,
      religionId: personalDetails.religionId || null,
      affiliateDisciplineId: professionalDetails.affiliateDisciplineId || null,
      experience: professionalDetails.experience ?? null,
      therapyModalities: nullIfBlank(professionalDetails.therapyModalities),
      bhfRegistration: nullIfBlank(professionalDetails.bhfRegistration),
      regulatoryRegistrationNumber: nullIfBlank(professionalDetails.regulatoryRegistrationNumber),
      companyName: nullIfBlank(professionalDetails.companyName),
      trainedByAffiliate: professionalDetails.trainedByAffiliate ?? false,
      isAvailableForReferrals: professionalDetails.isAvailableForReferrals ?? true,
      maxActiveCaseload: toNullableNumber(professionalDetails.maxActiveCaseload),
      wheelchairFriendly: accessibility.wheelchairFriendly ?? false,
      signLanguageFriendly: accessibility.signLanguageFriendly ?? false,
      languages: [],
      counsellingMedia: [],
      expertises: [],
      interventionTypes: (professionalDetails.interventionTypeIds ?? []).map((interventionTypeId) => ({
        interventionTypeId,
      })),
      suburbs: this.suburbsForm.getRawValue().map((suburb) => ({
        suburb: (suburb.suburb ?? '').trim(),
        provinceId: suburb.provinceId || null,
        travelRadiusKm: toNullableNumber(suburb.travelRadiusKm),
        isPrimary: suburb.isPrimary ?? false,
      })),
    };

    this.submitted.emit(request);
  }

  protected addSuburb(value?: AffiliateResponse['suburbs'][number]): void {
    this.suburbsForm.push(this.formBuilder.group({
      suburb: [value?.suburb ?? '', Validators.required],
      provinceId: [value?.provinceId ?? ''],
      travelRadiusKm: [value?.travelRadiusKm ?? null, Validators.min(0)],
      isPrimary: [value?.isPrimary ?? false],
    }));
  }

  protected removeSuburb(index: number): void {
    this.suburbsForm.removeAt(index);
  }

  
}

function toNullableNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}
